import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import ControlledSelect from "../../../components/ControlledSelect";
import { useForm } from "react-hook-form";
import { useOrderStatusUpdateMutation } from "../../../redux/services/orderService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import './index.css'
import ControlledRowTextField from "../../../components/ControlledRowTextField";
import { useSelector } from "react-redux";

interface StatusUpdateModalProps {
    isOpen: boolean;
    toggle: () => void;
    order: any;
    refreshOrderList?: () => void;
    isChildOrder?: boolean;
    isParentOrder?: boolean; // Add this prop
}

export interface StatusUpdateFormData {
    orderId: string;
    orderStatus: { value: string; label: string };
    updateNote: string;
}

const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
    isOpen,
    toggle,
    order,
    refreshOrderList,
    isChildOrder = false,
    isParentOrder = false, // Default to false
}) => {
    const deliveryCartId = useSelector((state: any) => state.restaurant.selectedRestaurantDetails.deliveryCartId || state.restaurant.data.deliveryCartId);
    const orderCartId = order?.deliveryCart?.cartId || order?.cart?.cartId;
    const isOrderFromRestaurant = order.isDeliveryOrder && (deliveryCartId === orderCartId) && isChildOrder;
    const [isLoading, setIsLoading] = useState(false);
    const {
        control,
        handleSubmit,
        setValue,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        formState: { errors },
    } = useForm<StatusUpdateFormData>();

    useEffect(() => {
        if (order) {
            const nextStatus = getNextStatus(order.orderStatus, isParentOrder);
            setValue("orderId", order.id);
            setValue("orderStatus", {
                value: nextStatus?.value || order.orderStatus,
                label: nextStatus?.label || order.orderStatus,
            });
            setValue("updateNote", order.updateNote || "");
        }
    }, [order, setValue, isParentOrder]);

    const getNextStatus = (currentStatus: string, isParent: boolean) => {
        if (!currentStatus) return null;
        
        const currentStatusLower = currentStatus.toLowerCase();
        
        // For parent orders, default to "Cancelled"
        if (isParent && currentStatusLower !== "completed") {
            return { value: "Cancelled", label: "Cancelled" };
        }
        
        // Define next status mappings for better control
        const statusTransitions: { [key: string]: { value: string; label: string } } = {
            "tentative": { value: "ApprovedByRestaurant", label: "Approved By Restaurant" },
            "pending": { value: "ApprovedByRestaurant", label: "Approved By Restaurant" },
            "acceptedbycart": { value: "ApprovedByRestaurant", label: "Approved By Restaurant" },
            "approvedbyrestaurant": { value: "Preparing", label: "Preparing" },
            "collecting": { value: "Preparing", label: "Preparing" },
            "preparing": { value: "ReadyAtKitchen", label: "Ready At Kitchen" },
            "readyatkitchen": { value: "IssuedByKitchen", label: "Issued By Kitchen" },
            // "readyatkitchen": { value: "PackingComplete", label: "Packing Complete" },
            // "packingComplete": { value: "PackingComplete", label: "Packing Complete" },
            "issuedbykitchen": { value: "ArrivedAtCart", label: "Arrived At Cart" },
            "arrivedatcart": { value: "PackingComplete", label: "Packing Complete" },
            "packingcomplete": { value: "Completed", label: "Completed" },
            "inappcart": { value: "Preparing", label: "Preparing" },
        };
        
        return statusTransitions[currentStatusLower] || null;
    };

    const filteredOptions = useMemo(() => {
        if (!order?.orderStatus) return [];
        const currentStatus = order.orderStatus.toLowerCase();
        const createdBy = order.orderCreatedBy.toLowerCase();
        
        // For parent orders, only show "Cancelled" option if not already completed
        if (isParentOrder) {
            if (currentStatus === "completed") {
                return []; // No options for completed parent orders
            }
            return [
                { value: "Cancelled", label: "Cancelled" }
            ];
        }
        
        // Define base options for each status
        const baseOptions = {
            "inappcart": [
                { value: "Preparing", label: "Preparing" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            "pending": createdBy === "customer" ? [
                { value: "ApprovedByRestaurant", label: "Approved By Restaurant" },
                { value: "Cancelled", label: "Cancelled" },
            ] : createdBy === "cart person" ? [
                { value: "Collecting", label: "Collecting" },
                { value: "Cancelled", label: "Cancelled" },
            ] : [],
            "acceptedbycart": [
                { value: "ApprovedByRestaurant", label: "Approved By Restaurant" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            "approvedbyrestaurant": [
                { value: "Preparing", label: "Preparing" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            "collecting": [
                { value: "Preparing", label: "Preparing" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            "preparing": [
                { value: "ReadyAtKitchen", label: "Ready At Kitchen" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            "tentative": [
                { value: "ApprovedByRestaurant", label: "Approved By Restaurant" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            "readyatkitchen": [
                { value: "PackingCompleted", label: "Packing Completed" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            "packingcomplete": [
                { value: "ArrivedAtCart", label: "Arrived At Cart" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            "arrivedatcart": [
                { value: "Completed", label: "Completed" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            "issuedbykitchen": [
                { value: "Completed", label: "Completed" },
                { value: "Cancelled", label: "Cancelled" },
            ]
        };
        
        // Get options for current status
        const options = baseOptions[currentStatus as keyof typeof baseOptions] || [];

        if (isOrderFromRestaurant) {
            return options;
        }
        
        // If it's a child order, remove the "Cancelled" option
        if (isChildOrder) {
            return options.filter(option => option.value !== "Cancelled");
        }
        
        return options;
    }, [order, isChildOrder, isParentOrder, isOrderFromRestaurant]);

    const [updateOrderStatus] = useOrderStatusUpdateMutation();

    const onSubmit = async (data: StatusUpdateFormData) => {
        const isCurrentlyTentative = (order?.orderStatus || "").toLowerCase() === "tentative";
        const isChangingAwayFromTentative = isCurrentlyTentative && (data.orderStatus.value || "").toLowerCase() !== "tentative";
        const isCancellingParent = isParentOrder && data.orderStatus.value === "Cancelled";

        if (isCancellingParent) {
            const result = await Swal.fire({
                title: "Cancel Parent Order?",
                text: "This will cancel ALL child orders as well. This action cannot be undone.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, cancel all orders",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                confirmButtonColor: "#d33",
            });

            if (!result.isConfirmed) {
                return;
            }
        } else if (isChangingAwayFromTentative) {
            const result = await Swal.fire({
                title: "Change from Tentative?",
                text: "You won't be able to edit this order anymore.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, continue",
                cancelButtonText: "Cancel",
                reverseButtons: true,
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        const obj = {
            orderId: order?.id,
            orderStatus: data.orderStatus.value,
            updateNote: data.updateNote,
        };

        try {
            setIsLoading(true);
            const updateOrderStatusResponse = await updateOrderStatus(obj);
            setIsLoading(false);

            if (
                "data" in updateOrderStatusResponse &&
                updateOrderStatusResponse.data === "OK"
            ) {
                toast.success("Order Status Has Updated");
                toggle();
                if (refreshOrderList) {
                    refreshOrderList();
                }
            } else if ("data" in updateOrderStatusResponse) {
                toast.warning("Something Went Wrong");
            } else if ("error" in updateOrderStatusResponse) {
                console.error("Error response from server:", updateOrderStatusResponse.error);
                toast.error("Failed to update order status. Please try again.");
            }
        } catch (error) {
            toast.error("Failed to update order status. Please try again.");
            setIsLoading(false);
                }
    };
    
    const shouldDisableButton = useMemo(() => {
        if (!order?.orderStatus || !order?.orderCreatedBy) return true;
        const currentStatus = order.orderStatus.toLowerCase();

        // For parent orders, disable if already completed
        if (isParentOrder) {
            return currentStatus === "completed";
        }

        return ["packingcomplete", "arrived", "completed"].includes(currentStatus);
    }, [order, isParentOrder]);


    return (
        <Modal
            show={isOpen}
            onHide={toggle}
            centered
            size="lg"
            backdrop="static"
            keyboard={false}
        >
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isParentOrder ? "Cancel Parent Order" : "Update Order Status"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mt-3 mb-3">
                        <ControlledRowTextField
                            label={"Update Note"}
                            inputName={"updateNote"}
                            placeholder={"Enter Update Note"}
                            inputType={"text"}
                            control={control}
                            defaultValue={order?.updateNote}
                        />
                    </div>
                    <div className="mt-3 mb-3">
                        <ControlledSelect
                            label={"Choose Order Status"}
                            inputName={"orderStatus"}
                            control={control}
                            isMulti={false}
                            optionsList={filteredOptions}
                        />
                    </div>
                    <div className='d-flex justify-content-end'>
                        {isLoading ? (
                            <Button variant="danger" disabled={isLoading}>
                                <FontAwesomeIcon icon={faSpinner} spin /> Updating...
                            </Button>
                        ) : (
                            <Button className="Submit-button"
                                variant={isParentOrder ? "warning" : "danger"}
                                type="submit"
                                disabled={shouldDisableButton}
                            >
                                {isParentOrder ? "Cancel Order" : "Update"}
                            </Button>
                        )}
                    </div>

                </Modal.Body>
            </Form>
        </Modal>
    );
};

export default StatusUpdateModal;